/**
 * API client for website detail popups (public endpoint).
 */

import unauthenticatedApi from "@/utils/unauthenticatedAxios";
import api from "@/utils/axios";
import type { MapLayerId } from "@/components/search-page/region-filter-section/mapHelpers";

const WEBSITE_DETAIL_TIMEOUT_MS = 12_000;
const WEBSITE_SIMILAR_TIMEOUT_MS = 60_000;

export type CategoryPathEntry = {
  id: string;
  slug: string;
  nameDe: string;
  nameEn: string;
  depth?: number | null;
};

export type WebsiteDetailPayload = {
  website: {
    id: string;
    title: string;
    description: string;
    urlLandingPage?: string | null;
    hostname?: string | null;
    address?: string | null;
    zipCode?: string | null;
    status?: string | null;
    addedAt?: string | null;
    addedBy?: string | null;
    pageviewsTotal?: number | null;
    geoLat?: number | null;
    geoLon?: number | null;
    regionName?: string | null;
    regionNameEn?: string | null;
    regionPathNames?: string[] | null;
    regionPathNamesEn?: string[] | null;
    regionLayerId?: MapLayerId | null;
    regionStateName?: string | null;
    regionStateNameEn?: string | null;
    regionKey?: string | null;
    categoryPath?: CategoryPathEntry[] | null;
  };
  similarWebsites: Array<{
    id: string;
    title: string;
    description: string;
    urlLandingPage?: string | null;
    hostname?: string | null;
    address?: string | null;
    zipCode?: string | null;
    pageviewsTotal?: number | null;
    regionName?: string | null;
    regionNameEn?: string | null;
    regionPathNames?: string[] | null;
    regionPathNamesEn?: string[] | null;
    categoryPath?: CategoryPathEntry[] | null;
  }>;
};

export type WebsiteCoreDetail = WebsiteDetailPayload["website"];
export type SimilarWebsiteDetail = WebsiteDetailPayload["similarWebsites"][number];

export type SimilarRankingMethod =
  | "METADATA_BASELINE"
  | "BM25_TEXT"
  | "SEMANTIC_EMBEDDING"
  | "HYBRID_RRF";

export type RecommendationItem = {
  stableId?: string | null;
  id: string;
  title?: string | null;
  description?: string | null;
  urlLandingPage?: string | null;
  hostname?: string | null;
  regionName?: string | null;
  categoryPath: string[];
  score?: number | null;
  rank: number;
  evidence?: string | null;
};

export type RecommendationMethodGroup = {
  method: SimilarRankingMethod;
  label: string;
  available: boolean;
  note?: string | null;
  items: RecommendationItem[];
};

export type RecommendationCandidateComparison = {
  stableId?: string | null;
  id: string;
  title?: string | null;
  hostname?: string | null;
  bestRank?: number | null;
  rankSpread?: number | null;
  ranksByMethod: Partial<Record<SimilarRankingMethod, number>>;
  scoresByMethod: Partial<Record<SimilarRankingMethod, number>>;
  evidence?: string | null;
};

export type RecommendationRunMetadata = {
  datasetSha256?: string | null;
  embeddingModelId?: string | null;
  textFields?: string | null;
  hybridRule?: string | null;
  hybridConfig?: string | null;
  bm25Formula?: string | null;
  bm25K1?: number | null;
  bm25B?: number | null;
  semanticFormula?: string | null;
  hybridFormula?: string | null;
  hybridInputs?: string[] | null;
  evaluationK?: number | null;
  relevanceScale?: string | null;
  generatedAt?: string | null;
  note?: string | null;
};

export type SimilarRecommendationPayload = {
  websiteId: string;
  researchMode: boolean;
  methodGroups: RecommendationMethodGroup[];
  candidateComparisons: RecommendationCandidateComparison[];
  metadata: RecommendationRunMetadata;
};

export async function fetchWebsiteDetail(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<WebsiteDetailPayload> {
  const response = await unauthenticatedApi.get<WebsiteDetailPayload>(
    `/public/websites/${encodeURIComponent(id)}/detail`,
    {
      headers: { Accept: "application/json" },
      signal: options.signal,
      timeout: WEBSITE_DETAIL_TIMEOUT_MS,
    },
  );
  return response.data;
}

export async function fetchWebsiteCoreDetail(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<WebsiteCoreDetail> {
  const response = await unauthenticatedApi.get<WebsiteCoreDetail>(
    `/public/websites/${encodeURIComponent(id)}/detail/website`,
    {
      headers: { Accept: "application/json" },
      signal: options.signal,
      timeout: WEBSITE_DETAIL_TIMEOUT_MS,
    },
  );
  return response.data;
}

export async function fetchWebsiteSimilar(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<SimilarWebsiteDetail[]> {
  const response = await unauthenticatedApi.get<SimilarWebsiteDetail[]>(
    `/public/websites/${encodeURIComponent(id)}/detail/similar`,
    {
      headers: { Accept: "application/json" },
      signal: options.signal,
      timeout: WEBSITE_SIMILAR_TIMEOUT_MS,
    },
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function fetchWebsiteRecommendations(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<SimilarRecommendationPayload> {
  const response = await unauthenticatedApi.get<SimilarRecommendationPayload>(
    `/public/websites/${encodeURIComponent(id)}/detail/recommendations`,
    {
      headers: { Accept: "application/json" },
      signal: options.signal,
      timeout: WEBSITE_SIMILAR_TIMEOUT_MS,
    },
  );
  return response.data;
}

export async function fetchAdminWebsiteRecommendations(
  id: string,
  options: { signal?: AbortSignal } = {},
): Promise<SimilarRecommendationPayload> {
  const response = await api.get<SimilarRecommendationPayload>(
    `/admin/similar-recommendations/${encodeURIComponent(id)}/comparison`,
    {
      headers: { Accept: "application/json" },
      signal: options.signal,
      timeout: WEBSITE_SIMILAR_TIMEOUT_MS,
    },
  );
  return response.data;
}
